import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Theme provider.
 *
 * TWO VALUES, AND THE DIFFERENCE MATTERS:
 *
 *   theme          the user's PREFERENCE — 'light' | 'dark' | 'system'.
 *                  This is what the theme picker highlights, and the only
 *                  thing persisted.
 *   resolvedTheme  what is actually on screen — 'light' | 'dark'. 'system' is
 *                  resolved against prefers-color-scheme here, in one place.
 *
 * Every component that picks a COLOUR must read `isDark` / `resolvedTheme`,
 * never `theme === 'dark'`: with the preference set to System on a dark OS,
 * `theme` is the string 'system', so `theme === 'dark'` is false and the
 * component renders light text onto the dark surface its neighbours drew —
 * text the same colour as its background. That bug is why this comment exists.
 *
 * THE `dark` CLASS IS NOT OPTIONAL. tailwind.config.js sets
 * `darkMode: "class"`, so every `dark:` utility in the app is dead unless the
 * class is on <html>. Applying it is this provider's job and nothing else's.
 */

const ThemeContext = createContext();

const STORAGE_KEY = 'theme';
const VALID_THEMES = ['light', 'dark', 'system'];

/** prefers-color-scheme, guarded for SSR and very old browsers. */
const systemPrefersDark = () => {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (_) {
    return false;
  }
};

/** 'light' | 'dark' | 'system' -> 'light' | 'dark'. */
export const resolveTheme = (preference) => {
  if (preference === 'dark') return 'dark';
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return 'light';
};

/** The stored preference, or 'light'. Read synchronously so the first paint is already correct. */
const readStoredTheme = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return VALID_THEMES.includes(saved) ? saved : 'light';
  } catch (_) {
    return 'light';
  }
};

/**
 * Put the resolved theme on the document: the `dark` class Tailwind's
 * class strategy switches on, plus `color-scheme` so form controls,
 * scrollbars and the canvas behind the page follow too.
 */
export const applyThemeToDocument = (resolved) => {
  if (typeof document === 'undefined' || !document.documentElement) return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved === 'dark' ? 'dark' : 'light';
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialised from storage rather than defaulted to 'light' and corrected in
  // an effect: the old order painted one light frame before flipping to dark.
  const [theme, setTheme] = useState(readStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(readStoredTheme()));

  const applyTheme = useCallback((newTheme) => {
    const resolved = resolveTheme(newTheme);
    setResolvedTheme(resolved);
    applyThemeToDocument(resolved);
    return resolved;
  }, []);

  // Apply on mount, and whenever the preference changes.
  useEffect(() => { applyTheme(theme); }, [theme, applyTheme]);

  const changeTheme = useCallback((newTheme) => {
    const next = VALID_THEMES.includes(newTheme) ? newTheme : 'light';
    setTheme(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* private mode */ }
    applyTheme(next);
  }, [applyTheme]);

  // Follow the OS while the preference is 'system'.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => { if (theme === 'system') applyTheme('system'); };
    // addEventListener is not available on Safari < 14's MediaQueryList.
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [theme, applyTheme]);

  const value = useMemo(() => ({
    theme,
    resolvedTheme,
    changeTheme,
    isDark: resolvedTheme === 'dark',
    isLight: resolvedTheme === 'light',
  }), [theme, resolvedTheme, changeTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
